/**
 * The install state the page and the service worker share (issue #3; CONTEXT.md, "Install",
 * "Staged update"): which build of the app shell and which Snapshot this launch reads, and
 * which pair waits for a launch with no Session open. It lives as one JSON entry in a Cache
 * API cache of its own, the one store both contexts can read without a schema, and the worker
 * re-reads it when the page says it changed (ADR-0016).
 */

/** One installed pair: an app shell build and the Snapshot it was deployed with. */
export interface Installed {
  /** The build id stamped into the shell and its service worker (`__BUILD_ID__`). */
  build: string;
  /** The content hash of `manifest.json`, the name of the Snapshot's cache. */
  data: string;
  /** The Snapshot's release tag, for the About screen. */
  release: string;
  /** What the Snapshot's cache holds. */
  totalBytes: number;
  files: number;
  /** When the pair was recorded (ISO). */
  at: string;
}

export interface PwaState {
  /** What this launch reads; null before the first install finishes. */
  active: Installed | null;
  /** A complete update waiting for a launch with no Session open. */
  staged: Installed | null;
  /** The Snapshot cache a download is filling, kept from the worker's clean-up until it is recorded. */
  installing: string | null;
  /** When the network was last asked for a newer build (ISO). */
  lastCheck: string | null;
}

export const STATE_CACHE = "pwa-state";
export const STATE_URL = "/__pwa/state";
export const STATE_FORMAT = 1;

export function emptyState(): PwaState {
  return { active: null, staged: null, installing: null, lastCheck: null };
}

/** The cache holding one Snapshot's files, named by the manifest's content hash. */
export function dataCacheName(hash: string): string {
  return `data-${hash}`;
}

/** The cache holding one build's app shell. */
export function shellCacheName(build: string): string {
  return `shell-${build}`;
}

export function isDataCache(name: string): boolean {
  return name.startsWith("data-");
}

export function isShellCache(name: string): boolean {
  return name.startsWith("shell-");
}

/** The caches a clean-up must leave alone: the state, everything the state names, and the worker's own shell. */
export function cachesToKeep(state: PwaState, selfBuild?: string): Set<string> {
  const keep = new Set<string>([STATE_CACHE]);
  for (const pair of [state.active, state.staged]) {
    if (!pair) continue;
    keep.add(dataCacheName(pair.data));
    keep.add(shellCacheName(pair.build));
  }
  if (state.installing) keep.add(dataCacheName(state.installing));
  if (selfBuild) keep.add(shellCacheName(selfBuild));
  return keep;
}

function isInstalled(v: unknown): v is Installed {
  const o = v as Installed | null;
  return !!o && typeof o === "object" && typeof o.build === "string" && typeof o.data === "string" && typeof o.release === "string";
}

/** Parse a stored state, falling back to the empty state for anything unreadable. */
export function parseState(text: string | null | undefined): PwaState {
  if (!text) return emptyState();
  try {
    const o = JSON.parse(text) as Partial<PwaState> & { format?: number };
    if (o.format !== STATE_FORMAT) return emptyState();
    return {
      active: isInstalled(o.active) ? o.active : null,
      staged: isInstalled(o.staged) ? o.staged : null,
      installing: typeof o.installing === "string" ? o.installing : null,
      lastCheck: typeof o.lastCheck === "string" ? o.lastCheck : null,
    };
  } catch {
    return emptyState();
  }
}

export async function readState(storage: CacheStorage): Promise<PwaState> {
  const cache = await storage.open(STATE_CACHE);
  const res = await cache.match(STATE_URL);
  return parseState(res ? await res.text() : null);
}

export async function writeState(storage: CacheStorage, state: PwaState): Promise<void> {
  const cache = await storage.open(STATE_CACHE);
  const body = JSON.stringify({ format: STATE_FORMAT, ...state });
  await cache.put(STATE_URL, new Response(body, { headers: { "content-type": "application/json" } }));
}

/** Delete every cache the state does not name. Returns the names deleted. */
export async function pruneCaches(storage: CacheStorage, state: PwaState, selfBuild?: string): Promise<string[]> {
  const keep = cachesToKeep(state, selfBuild);
  const gone: string[] = [];
  for (const name of await storage.keys()) {
    if (keep.has(name)) continue;
    if (!isDataCache(name) && !isShellCache(name)) continue;
    if (await storage.delete(name)) gone.push(name);
  }
  return gone;
}
